import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Qs from 'qs';
import { Button } from 'antd';
import { QuestionOutlined } from '@ant-design/icons';
import { ArrowLeft, Upload } from 'lucide-react';
import { useIntl } from 'react-intl';
import Data from './data/Data';
import Task from './task/Task';
import Setting from './setting/Setting';
import {
  actionGetProjectDetail,
  actionSetProjectDetail,
} from '../project/ProjectAction';
import { STUDY_TABS, USER_ROLES, routes } from '../../utils/constants/config';
import { isEmpty } from '../../utils/helpers';
import { isProjectOwner } from '../system/systemAction';
import './StudyList.scss';

const StudyList = (props) => {
  const { formatMessage: t } = useIntl();
  const [selectedTab, setSelectedTab] = useState({});

  const {
    match = {},
    userInfo,
    location = {},
    currentProject,
    viewMode,
  } = props;
  const urlParams = Qs.parse(location.search, { ignoreQueryPrefix: true });

  const projectId = match.params?.projectId;
  const isPO = isProjectOwner(currentProject, userInfo);
  const isViewAsPO = viewMode === USER_ROLES.PROJECT_OWNER && isPO;

  const tabList = [
    { key: STUDY_TABS.DATA, name: t({ id: 'IDS_DATA' }), isPO: true },
    { key: STUDY_TABS.TASK, name: t({ id: 'IDS_TASKS' }), isPO: true },
    { key: STUDY_TABS.SETTING, name: t({ id: 'IDS_SETTING' }), isPO: true },
    { key: STUDY_TABS.ANNOTATE, name: t({ id: 'IDS_ANNOTATE' }) },
    { key: STUDY_TABS.REVIEW, name: t({ id: 'IDS_REVIEW' }) },
  ];

  useEffect(() => {
    if (projectId) {
      props.actionGetProjectDetail(projectId);
    }
    return () => {
      props.actionSetProjectDetail({});
    };
    // eslint-disable-next-line
  }, [projectId]);

  useEffect(() => {
    if (!isEmpty(userInfo) && viewMode) {
      if (isViewAsPO) {
        if (urlParams?.tab === STUDY_TABS.SETTING.toLocaleLowerCase()) {
          handleReplaceState(tabList[2].key);
          setSelectedTab(tabList[2]);
        } else if (urlParams?.tab === STUDY_TABS.TASK.toLocaleLowerCase()) {
          handleReplaceState(tabList[1].key);
          setSelectedTab(tabList[1]);
        } else {
          handleReplaceState(tabList[0].key);
          setSelectedTab(tabList[0]);
        }
      } else {
        if (urlParams?.tab === STUDY_TABS.REVIEW.toLocaleLowerCase()) {
          handleReplaceState(tabList[4].key);
          setSelectedTab(tabList[4]);
        } else {
          handleReplaceState(tabList[3].key);
          setSelectedTab(tabList[3]);
        }
      }
    }
    // eslint-disable-next-line
  }, [viewMode, userInfo, isPO, location.search]);

  const handleReplaceState = (tab = '') => {
    window.history.replaceState(
      null,
      null,
      window.location.pathname + '?tab=' + tab.toLocaleLowerCase()
    );
  };

  const handleClickTab = (tab = {}) => {
    handleReplaceState(tab.key);
    setSelectedTab(tab);
  };

  if (isEmpty(userInfo) || isEmpty(currentProject)) return null;



  return (
    <div className="common-style-page study-list-page">
      <div className="page-inner">
        {/* Project Header */}
        <div className="project-header">
          <div className="project-header-left">
            <button
              className="back-arrow-btn"
              onClick={() => props.history.push(routes.PROJECTS)}
            >
              <ArrowLeft size={20} />
            </button>
            <div className="project-info">
              <h1 className="project-title">{currentProject?.name || ''}</h1>
              {currentProject?.description && (
                <p className="project-description">{currentProject.description}</p>
              )}
            </div>
          </div>
          {/* "Add to Project" button — sits on the right of the project
           * header row, only visible on the Data tab.  Triggers the
           * existing UploadProjectStudyModal at App level via a window
           * CustomEvent (App.js is already listening), so no prop
           * drilling and no duplicate modal logic. */}
          {selectedTab.key === STUDY_TABS.DATA && isViewAsPO && (
            <div className="project-header-right">
              <button
                className="project-header-add-btn"
                onClick={() => {
                  try {
                    window.dispatchEvent(new CustomEvent('open-project-upload-modal'));
                  } catch (e) { /* CustomEvent ctor unavailable in very old browsers */ }
                }}
              >
                <Upload size={14} />
                Add to Project
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="tabs-bar">
          <div className="tabs-list">
            {tabList.map((tab) => {
              if (isViewAsPO && tab.isPO) {
                return (
                  <button
                    key={tab.key}
                    className={`tab-btn ${
                      tab.key === selectedTab.key ? 'tab-active' : ''
                    }`}
                    onClick={() => handleClickTab(tab)}
                  >
                    {tab.name}
                    {tab.key === selectedTab.key && (
                      <span className="tab-underline" />
                    )}
                  </button>
                );
              } else if (!isViewAsPO && !tab.isPO) {
                return (
                  <button
                    key={tab.key}
                    className={`tab-btn ${
                      tab.key === selectedTab.key ? 'tab-active' : ''
                    }`}
                    onClick={() => handleClickTab(tab)}
                  >
                    {tab.name}
                    {tab.key === selectedTab.key && (
                      <span className="tab-underline" />
                    )}
                  </button>
                );
              } else {
                return null;
              }
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="page-content">
          <div className="tab-content">
          {selectedTab.key === STUDY_TABS.DATA && isViewAsPO && (
            <Data projectId={projectId} />
          )}
          {(selectedTab.key === STUDY_TABS.TASK ||
            selectedTab.key === STUDY_TABS.ANNOTATE ||
            selectedTab.key === STUDY_TABS.REVIEW) && (
            <Task projectId={projectId} typeTask={selectedTab?.key} />
          )}
          {selectedTab.key === STUDY_TABS.SETTING && isViewAsPO && (
            <Setting projectId={projectId} />
          )}
          </div>
        </div>
      </div>

      {currentProject?.document_link && (
        <div className="fixed-widgets">
          <Button
            type="primary"
            shape="circle"
            className="btn-open-guide"
            icon={<QuestionOutlined />}
            onClick={() => {
              window.open(currentProject?.document_link, '_blank');
            }}
          />
        </div>
      )}
    </div>
  );
};

export default connect(
  (state) => ({
    userInfo: state.system.profile,
    viewMode: state.system.viewMode,
    currentProject: state.project.currentProject,
  }),
  { actionGetProjectDetail, actionSetProjectDetail }
)(withRouter(StudyList));
